// src/components/SetPasswordPage.js
//
// Public page that lets a user set their OWN password from an emailed link
// (the invite/reset link generated by the createStaffUser Cloud Function). The
// link carries a Firebase `oobCode`; we validate it, let the user choose a
// password, then:
//   1. confirmPasswordReset   — set the new password in Identity Platform
//   2. sign in + stamp        — record the rotation clock via recordPasswordChange
//   3. sign out               — so they log in normally (password + OTP) next
//
// No identity changes: the account keeps the same email/UID, so all existing
// roles and patient associations are untouched.

import { useEffect, useState } from "react";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth } from "../firebase";
import { Lock, Loader2, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";

const RECORD_PWD_CHANGE_URL =
  `${FUNCTIONS_BASE}/recordPasswordChange`;

// Keep in sync with ChangePasswordPage. Strict but not hostile.
const passwordProblem = (pwd) => {
  if (pwd.length < 10) return "Use at least 10 characters.";
  if (!/[a-z]/.test(pwd) || !/[A-Z]/.test(pwd))
    return "Include both upper- and lower-case letters.";
  if (!/[0-9]/.test(pwd)) return "Include at least one number.";
  return null;
};

const SetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = params.get("oobCode") || "";

  const [stage, setStage] = useState("verifying"); // verifying | form | saving | done | invalid
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  // Validate the link on mount and discover whose account it is.
  useEffect(() => {
    let active = true;
    if (!oobCode) {
      setStage("invalid");
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((mail) => {
        if (!active) return;
        setEmail((mail || "").toLowerCase());
        setStage("form");
      })
      .catch(() => active && setStage("invalid"));
    return () => {
      active = false;
    };
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const problem = passwordProblem(pwd);
    if (problem) return setError(problem);
    if (pwd !== confirm) return setError("Passwords don't match.");

    setStage("saving");
    try {
      // 1. Set the new password.
      await confirmPasswordReset(auth, oobCode, pwd);

      // 2. Sign in to stamp the rotation clock (server-trusted via ID token),
      //    then sign out so the normal password + OTP flow runs next time.
      try {
        const cred = await signInWithEmailAndPassword(auth, email, pwd);
        const idToken = await cred.user.getIdToken();
        await fetch(RECORD_PWD_CHANGE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({}),
        }).catch(() => {});
      } catch (_) {
        // Stamping is best-effort; the login clock fails open if it's missing.
      } finally {
        await signOut(auth).catch(() => {});
      }

      setStage("done");
    } catch (err) {
      console.error(err);
      setError(
        err?.code === "auth/expired-action-code" ||
          err?.code === "auth/invalid-action-code"
          ? "This link has expired. Ask an admin to resend your invite."
          : "Couldn't set your password. Please try again."
      );
      setStage("form");
    }
  };

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex items-center justify-center px-4">
      <div className="fixed inset-0 -z-10 overflow-hidden bg-gradient-to-br from-[#070418] via-[#0c0726] to-[#04030f]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.28),_transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(37,99,235,0.22),_transparent_55%)]" />
      </div>

      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-5">
          <ShieldCheck className="w-5 h-5 text-purple-300" />
          <h1 className="text-lg font-bold tracking-tight text-white">
            Research Portal — Set Password
          </h1>
        </div>

        <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(124,58,237,0.5)] p-5">
          {error && (
            <div className="mb-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {stage === "verifying" && (
            <div className="flex items-center justify-center gap-2 py-6 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying your link…
            </div>
          )}

          {stage === "invalid" && (
            <div className="text-center py-4 space-y-3">
              <AlertCircle className="w-8 h-8 text-rose-300 mx-auto" />
              <p className="text-sm text-slate-300">
                This link is invalid or has expired. Ask an admin to resend your
                invite.
              </p>
              <Link
                to="/login"
                className="inline-block text-sm text-purple-300 hover:text-purple-200 underline"
              >
                Back to login
              </Link>
            </div>
          )}

          {stage === "done" && (
            <div className="text-center py-4 space-y-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto" />
              <p className="text-sm text-slate-200">
                Your password is set. You can now sign in.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white text-sm font-semibold ring-1 ring-white/20"
              >
                Go to login
              </button>
            </div>
          )}

          {(stage === "form" || stage === "saving") && (
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <p className="text-xs text-slate-400">
                Setting a password for{" "}
                <strong className="text-white">{email}</strong>
              </p>

              <div>
                <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                  New password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="password"
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                  Confirm password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="password"
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                </div>
              </div>

              <p className="text-[11px] text-slate-500">
                At least 10 characters, with upper- and lower-case letters and a
                number.
              </p>

              <button
                type="submit"
                disabled={stage === "saving"}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white text-sm font-semibold ring-1 ring-white/20 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {stage === "saving" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                  </>
                ) : (
                  "Set password"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
